import { Component } from '@theme/component';

/**
 * Hero (sticky) section custom element.
 *
 * Wires up the play/pause control inside `<hero-sticky>`. Handles the
 * desktop/mobile video pair: picks the visible one via matchMedia, pauses the
 * hidden one to save bandwidth, and keeps the icon + aria-label in sync with
 * the video's playback state.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} [desktopVideo]
 * @property {HTMLVideoElement} [mobileVideo]
 * @property {HTMLButtonElement} [toggleButton]
 * @property {HTMLElement} [playIcon]
 * @property {HTMLElement} [pauseIcon]
 *
 * @extends Component<Refs>
 */
class HeroStickyComponent extends Component {
  /** Matches the section's `min-width: 722px` CSS breakpoint. */
  #desktopMq = window.matchMedia('(min-width: 722px)');

  connectedCallback() {
    super.connectedCallback();

    const active = this.#activeVideo();
    if (!active) return;

    // Pause the inactive video — saves bandwidth/CPU on hidden playback.
    for (const video of this.#allVideos()) {
      if (video !== active) video.pause();
    }

    // Make sure the active one is playing. Browser may have auto-paused it
    // (autoplay block, hidden tab). Ignore promise rejection — the user can
    // still tap the play button.
    const playPromise = active.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }

    // Keep button icon + aria-label in sync with the active video.
    active.addEventListener('play', this.#handlePlay);
    active.addEventListener('pause', this.#handlePause);
    this.#setState(!active.paused);
  }

  /**
   * Toggle the active video. Bound via `on:click="/togglePlayback"` in liquid.
   */
  togglePlayback = () => {
    const active = this.#activeVideo();
    if (!active) return;
    if (active.paused) active.play();
    else active.pause();
  };

  #handlePlay = () => this.#setState(true);
  #handlePause = () => this.#setState(false);

  #activeVideo() {
    const { desktopVideo, mobileVideo } = this.refs;
    if (desktopVideo && mobileVideo) {
      return this.#desktopMq.matches ? desktopVideo : mobileVideo;
    }
    return desktopVideo || mobileVideo;
  }

  #allVideos() {
    const { desktopVideo, mobileVideo } = this.refs;
    return [desktopVideo, mobileVideo].filter(Boolean);
  }

  #setState(isPlaying) {
    const { toggleButton, playIcon, pauseIcon } = this.refs;
    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    if (!toggleButton) return;
    const playLabel = toggleButton.getAttribute('data-play-label') || 'Play';
    const pauseLabel = toggleButton.getAttribute('data-pause-label') || 'Pause';
    toggleButton.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
  }
}

if (!customElements.get('hero-sticky')) {
  customElements.define('hero-sticky', HeroStickyComponent);
}
